/** API 请求失败（HTTP 4xx/5xx）时抛出的统一错误 */
export class ApiClientError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.status = status;
        this.code = code;
    }
}
/** 从响应中提取错误信息 */
function toApiError(res) {
    return res
        .json()
        .then((body) => new ApiClientError(res.status, body.error.code, body.error.message))
        .catch(() => new ApiClientError(res.status, 'INTERNAL_ERROR', `请求失败（${res.status}）`));
}
/**
 * 发起 API 请求。
 * @param path 以 /api 开头的路径
 * @param init 请求配置（method/body 等）；body 为对象时自动 JSON 序列化
 */
export async function apiFetch(path, init = {}) {
    const { body, headers, ...rest } = init;
    const res = await fetch(path, {
        ...rest,
        headers: {
            ...headers,
            ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok)
        throw await toApiError(res);
    // 空响应（如 logout）返回 null
    if (res.status === 204)
        return undefined;
    return (await res.json());
}
// ---------- 认证 / 我的 ----------
/** GET /api/captcha：获取算术验证码 */
export const getCaptcha = () => apiFetch('/api/captcha');
/** POST /api/auth/register：注册并自动登录 */
export const register = (req) => apiFetch('/api/auth/register', { method: 'POST', body: req });
/** POST /api/auth/login：登录 */
export const login = (req) => apiFetch('/api/auth/login', { method: 'POST', body: req });
/** POST /api/auth/logout：登出 */
export const logout = () => apiFetch('/api/auth/logout', { method: 'POST' });
/** GET /api/auth/me：当前登录用户；未登录抛 401 */
export const fetchMe = () => apiFetch('/api/auth/me');
/** PATCH /api/me：修改昵称 / 头像 */
export const updateMe = (req) => apiFetch('/api/me', { method: 'PATCH', body: req });
/** POST /api/divination/records：存档一次占卜 */
export const saveDivination = (req) => apiFetch('/api/divination/records', { method: 'POST', body: req });
/** GET /api/divination/history：最近 100 条 */
export const fetchDivinationHistory = () => apiFetch('/api/divination/history');
/** DELETE /api/divination/history/:id */
export const deleteDivinationRecord = (id) => apiFetch(`/api/divination/history/${id}`, { method: 'DELETE' });
/** DELETE /api/divination/history：清空 */
export const clearDivinationHistory = () => apiFetch('/api/divination/history', { method: 'DELETE' });
/** GET /api/posts?board=&page=&size= */
export const fetchPosts = (params) => apiFetch(`/api/posts?board=${params.board}&page=${params.page ?? 1}&size=${params.size ?? 20}`);
/** POST /api/posts：发帖（board=announcement 会被服务端 403 拒绝，公告走管理端接口） */
export const createPost = (req) => apiFetch('/api/posts', { method: 'POST', body: req });
/** GET /api/posts/:id：详情 + 评论 */
export const fetchPostDetail = (id) => apiFetch(`/api/posts/${id}`);
/** PATCH /api/posts/:id：作者编辑帖子（标题可选；服务端落 updated_at 供「编辑于」展示） */
export const updatePost = (id, req) => apiFetch(`/api/posts/${id}`, { method: 'PATCH', body: req });
/** DELETE /api/posts/:id */
export const deletePost = (id) => apiFetch(`/api/posts/${id}`, { method: 'DELETE' });
/** POST /api/posts/:id/like / DELETE */
export const likePost = (id, liked) => apiFetch(`/api/posts/${id}/like`, { method: liked ? 'DELETE' : 'POST' });
/** POST /api/comments/:id/like / DELETE：评论点赞（幂等，返回最新计数） */
export const likeComment = (id, liked) => apiFetch(`/api/comments/${id}/like`, {
    method: liked ? 'DELETE' : 'POST',
});
/** POST /api/posts/:id/comments：回复（parent_id 为空 → L1） */
export const createComment = (postId, parentId, content) => apiFetch(`/api/posts/${postId}/comments`, {
    method: 'POST',
    body: { parent_id: parentId, content },
});
/** DELETE /api/comments/:id */
export const deleteComment = (id) => apiFetch(`/api/comments/${id}`, { method: 'DELETE' });
/** POST /api/reports：举报（content = 帖子/评论；avatar = 用户头像） */
export const createReport = (req) => apiFetch('/api/reports', { method: 'POST', body: req });
// ---------- 我的中心（M4） ----------
/** GET /api/me/stats：我的统计（帖子数 / 回复数 / 获赞数） */
export const fetchMyStats = () => apiFetch('/api/me/stats');
/** GET /api/me/posts */
export const fetchMyPosts = (page = 1) => apiFetch(`/api/me/posts?page=${page}`);
/** GET /api/me/comments */
export const fetchMyComments = (page = 1) => apiFetch(`/api/me/comments?page=${page}`);
/** GET /api/me/likes */
export const fetchMyLikes = (page = 1) => apiFetch(`/api/me/likes?page=${page}`);
/** GET /api/me/notifications：分页列表 + 未读数 */
export const fetchNotifications = (page = 1, size = 20) => apiFetch(`/api/me/notifications?page=${page}&size=${size}`);
/** POST /api/me/notifications/read：标记已读（全部或指定 id），返回最新未读数 */
export const markNotificationsRead = (opts) => apiFetch('/api/me/notifications/read', { method: 'POST', body: opts });
/** GET /api/me/notifications/unread-count：未读数（顶栏角标轮询） */
export const fetchNotificationUnread = () => apiFetch('/api/me/notifications/unread-count');
// ---------- 管理端（M5） ----------
/** POST /api/admin/login */
export const adminLogin = (username, password) => apiFetch('/api/admin/login', {
    method: 'POST',
    body: { username, password },
});
/** POST /api/admin/logout */
export const adminLogout = () => apiFetch('/api/admin/logout', { method: 'POST' });
/** GET /api/admin/stats */
export const adminStats = () => apiFetch('/api/admin/stats');
/** GET /api/admin/users（keyword 搜索 + status 筛选 + 分页） */
export const adminUsers = (keyword = '', status = '', page = 1, size = 20) => apiFetch(`/api/admin/users?keyword=${encodeURIComponent(keyword)}&status=${status}&page=${page}&size=${size}`);
/** PATCH /api/admin/users/:id（action 含处置动作；days = 天数；permanent = 永久，0006 起 days/permanent 二选一） */
export const adminUserAction = (id, action, mute_until, days, permanent) => apiFetch(`/api/admin/users/${id}`, {
    method: 'PATCH',
    body: { action, mute_until, days, permanent },
});
/** GET /api/admin/posts（board/置顶/隐藏筛选 + 分页） */
export const adminPosts = (board = '', pinned = '', page = 1, size = 20, hidden = '') => apiFetch(`/api/admin/posts?board=${board}&pinned=${pinned}&page=${page}&size=${size}&hidden=${hidden}`);
/** PATCH /api/admin/posts/:id/pin */
export const adminPinPost = (id, pinned) => apiFetch(`/api/admin/posts/${id}/pin`, { method: 'PATCH', body: { pinned } });
/** PATCH /api/admin/posts/:id/lock：只读锁定/解锁（锁定后用户端无法回复） */
export const adminLockPost = (id, locked) => apiFetch(`/api/admin/posts/${id}/lock`, { method: 'PATCH', body: { locked } });
/** PATCH /api/admin/posts/:id/hide：隐藏/取消隐藏（公众不可见，作者与管理端可见） */
export const adminHidePost = (id, hidden) => apiFetch(`/api/admin/posts/${id}/hide`, { method: 'PATCH', body: { hidden } });
/** POST /api/admin/announcements：发布公告帖（board=announcement，作者展示「官方」） */
export const adminCreateAnnouncement = (title, content) => apiFetch('/api/admin/announcements', {
    method: 'POST',
    body: { title, content },
});
/** DELETE /api/admin/posts/:id */
export const adminDeletePost = (id) => apiFetch(`/api/admin/posts/${id}`, { method: 'DELETE' });
/** GET /api/admin/comments（评论管理分页列表，含所属帖标题） */
export const adminComments = (page = 1, size = 20) => apiFetch(`/api/admin/comments?page=${page}&size=${size}`);
/** DELETE /api/admin/comments/:id（级联删除子回复） */
export const adminDeleteComment = (id) => apiFetch(`/api/admin/comments/${id}`, { method: 'DELETE' });
/** GET /api/admin/reports（status 筛选 + 分页；target_type = content | avatar；含作者信息） */
export const adminReports = (status = 'pending', page = 1, size = 20) => apiFetch(`/api/admin/reports?status=${status}&page=${page}&size=${size}`);
/** PATCH /api/admin/reports/:id（处置动作 + 期限：days 天数 / permanent 永久，见 worker reportActionSchema） */
export const adminHandleReport = (id, action, days, permanent) => apiFetch(`/api/admin/reports/${id}`, {
    method: 'PATCH',
    body: { action, days, permanent },
});
/** GET/PATCH /api/admin/settings */
export const adminSettings = () => apiFetch('/api/admin/settings');
export const adminUpdateSettings = (settings) => apiFetch('/api/admin/settings', { method: 'PATCH', body: settings });
//# sourceMappingURL=index.js.map